import { API_HEADERS } from '../../constants/api-constants';
import {
  exchangeAuth0Token,
  exchangeForTenant,
  refreshTokens,
  revokeTokens,
  type BrokerTokens,
} from './brokerClient';
import { tenantIdOf } from './maiaJwt';

// Refresh once 80% of the access-token TTL has elapsed (the broker's guidance).
// The active tenant refreshes automatically on a timer at this point; cached
// non-active tenants refresh lazily on switch-back if they are past it.
const REFRESH_AT_FRACTION = 0.8;
const STORAGE_KEY = 'maia_broker_session';

// One broker-minted session, keyed by the tenant it is scoped to. Timestamps are
// absolute epoch milliseconds so they survive a page reload unchanged.
export interface TenantSession {
  tenantId: string;
  accessToken: string;
  refreshToken: string;
  refreshAtMs: number;
  expiresAtMs: number;
}

interface PersistedState {
  activeTenantId: string | null;
  sessions: TenantSession[];
}

export interface BrokerSessionDeps {
  now: () => number;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  client: {
    exchangeAuth0Token: typeof exchangeAuth0Token;
    exchangeForTenant: typeof exchangeForTenant;
    refreshTokens: typeof refreshTokens;
    revokeTokens: typeof revokeTokens;
  };
  // The Auth0 access token minted for the broker audience — the subject of the
  // initial token exchange. Rejects when the user isn't authenticated.
  getSubjectToken: () => Promise<string>;
  // Schedules the active-tenant auto-refresh; returns a canceller. Injected so
  // tests can drive it without real timers.
  scheduleRefresh: (delayMs: number, run: () => void) => () => void;
  // Invoked when the session can no longer be renewed (revoked/expired/reuse) —
  // routes the user back to login.
  onSessionLost: () => void;
}

export interface BrokerSession {
  ensureSession: () => Promise<void>;
  ensureTenant: (tenantId: string) => Promise<void>;
  getValidMaiaAccessToken: () => Promise<string | null>;
  getActiveTenantId: () => string | null;
  tenantHeaderFor: (tenantId: string) => Record<string, string>;
  endBrokerSession: () => Promise<void>;
  clearBrokerSession: () => void;
}

export function createBrokerSession(deps: BrokerSessionDeps): BrokerSession {
  const sessions = new Map<string, TenantSession>();
  let activeTenantId: string | null = null;

  // Single-flight guards: the initial exchange and each per-tenant refresh run
  // at most once concurrently, so racing backend calls share one network round.
  let exchangeInFlight: Promise<void> | null = null;
  const refreshInFlight = new Map<string, Promise<TenantSession>>();
  let cancelActiveTimer: (() => void) | null = null;

  hydrate();

  function hydrate(): void {
    const raw = deps.storage?.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const state = JSON.parse(raw) as PersistedState;
      for (const session of state.sessions)
        sessions.set(session.tenantId, session);
      activeTenantId = state.activeTenantId;
    } catch {
      deps.storage?.removeItem(STORAGE_KEY);
    }
  }

  function persist(): void {
    if (!deps.storage) return;
    const state: PersistedState = {
      activeTenantId,
      sessions: [...sessions.values()],
    };
    deps.storage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  function activeSession(): TenantSession | null {
    return activeTenantId ? (sessions.get(activeTenantId) ?? null) : null;
  }

  function toSession(tokens: BrokerTokens): TenantSession {
    const now = deps.now();
    const tenantId = tenantIdOf(tokens.accessToken);
    if (!tenantId) {
      throw new Error('broker returned a token with no tenant claim');
    }
    return {
      tenantId,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      refreshAtMs: now + tokens.expiresInSeconds * 1000 * REFRESH_AT_FRACTION,
      expiresAtMs: now + tokens.expiresInSeconds * 1000,
    };
  }

  function store(tokens: BrokerTokens): TenantSession {
    const session = toSession(tokens);
    sessions.set(session.tenantId, session);
    persist();
    if (session.tenantId === activeTenantId) scheduleActiveRefresh();
    return session;
  }

  function setActive(tenantId: string): void {
    activeTenantId = tenantId;
    persist();
    scheduleActiveRefresh();
  }

  function isExpired(session: TenantSession): boolean {
    return deps.now() >= session.expiresAtMs;
  }

  function needsRefresh(session: TenantSession): boolean {
    return deps.now() >= session.refreshAtMs;
  }

  function scheduleActiveRefresh(): void {
    cancelActiveTimer?.();
    cancelActiveTimer = null;
    const session = activeSession();
    if (!session) return;

    const delay = Math.max(0, session.refreshAtMs - deps.now());
    cancelActiveTimer = deps.scheduleRefresh(delay, () => {
      // A switch may have changed the active tenant since this was scheduled;
      // if so, the newer setActive already owns the timer — do nothing.
      if (activeTenantId !== session.tenantId) return;
      refresh(session.tenantId)
        .then(() => scheduleActiveRefresh())
        .catch(() => handleSessionLost());
    });
  }

  function refresh(tenantId: string): Promise<TenantSession> {
    const existing = refreshInFlight.get(tenantId);
    if (existing) return existing;

    const session = sessions.get(tenantId);
    if (!session) return Promise.reject(new Error('no session for tenant'));

    const pending = deps.client
      .refreshTokens(session.refreshToken)
      .then((tokens) => store(tokens))
      .finally(() => refreshInFlight.delete(tenantId));
    refreshInFlight.set(tenantId, pending);
    return pending;
  }

  async function freshSubjectToken(session: TenantSession): Promise<string> {
    if (needsRefresh(session)) {
      const refreshed = await refresh(session.tenantId);
      return refreshed.accessToken;
    }
    return session.accessToken;
  }

  function handleSessionLost(): void {
    clearBrokerSession();
    deps.onSessionLost();
  }

  async function doInitialExchange(): Promise<void> {
    // A hydrated-but-stale active session is cheaper to refresh than to
    // re-exchange through Auth0; fall back to a full exchange only if that fails.
    const active = activeSession();
    if (active) {
      try {
        await refresh(active.tenantId);
        return;
      } catch {
        sessions.delete(active.tenantId);
      }
    }

    const subjectToken = await deps.getSubjectToken();
    const session = store(await deps.client.exchangeAuth0Token(subjectToken));
    setActive(session.tenantId);
  }

  function ensureSession(): Promise<void> {
    const active = activeSession();
    if (active && !isExpired(active)) return Promise.resolve();
    if (!exchangeInFlight) {
      exchangeInFlight = doInitialExchange().finally(() => {
        exchangeInFlight = null;
      });
    }
    return exchangeInFlight;
  }

  async function ensureTenant(tenantId: string): Promise<void> {
    await ensureSession();

    const cached = sessions.get(tenantId);
    if (cached && !needsRefresh(cached)) {
      setActive(tenantId);
      return;
    }
    if (cached) {
      // Past its refresh threshold (or expired): rotate its own lineage first.
      try {
        await refresh(tenantId);
        setActive(tenantId);
        return;
      } catch {
        // Idle/absolute timeout or a revoked family — fall through to re-mint.
        sessions.delete(tenantId);
      }
    }

    const active = activeSession();
    if (!active) throw new Error('no active session to switch from');
    const subject = await freshSubjectToken(active);
    const session = store(
      await deps.client.exchangeForTenant(subject, tenantId),
    );
    setActive(session.tenantId);
  }

  async function getValidMaiaAccessToken(): Promise<string | null> {
    try {
      await ensureSession();
    } catch (error) {
      console.error('broker session bootstrap failed:', error);
      return null;
    }

    const active = activeSession();
    if (!active) return null;
    if (!needsRefresh(active)) return active.accessToken;

    try {
      return (await refresh(active.tenantId)).accessToken;
    } catch {
      handleSessionLost();
      return null;
    }
  }

  function getActiveTenantId(): string | null {
    return activeTenantId;
  }

  // The Maia token's tenant is authoritative. A request for a different tenant
  // than the active token is a bug: silently substituting the token's tenant
  // would serve an unexpected tenant's data (looks like a leak, even though the
  // token is authorized for it). Fail loudly instead of dropping the header. See
  // attachMaiaAuth for the same rule on the axios-instance traffic.
  function tenantHeaderFor(tenantId: string): Record<string, string> {
    if (activeTenantId && tenantId !== activeTenantId) {
      throw new Error(
        `Tenant mismatch: request targets "${tenantId}" but the active Maia ` +
          `token is scoped to "${activeTenantId}". Switch tenants (ensureTenant) ` +
          `before calling.`,
      );
    }
    return { [API_HEADERS.TENANT_ID]: tenantId };
  }

  async function endBrokerSession(): Promise<void> {
    const refreshTokensToRevoke = [...sessions.values()].map(
      (session) => session.refreshToken,
    );
    clearBrokerSession();
    await Promise.allSettled(
      refreshTokensToRevoke.map((token) => deps.client.revokeTokens(token)),
    );
  }

  function clearBrokerSession(): void {
    cancelActiveTimer?.();
    cancelActiveTimer = null;
    sessions.clear();
    activeTenantId = null;
    deps.storage?.removeItem(STORAGE_KEY);
  }

  return {
    ensureSession,
    ensureTenant,
    getValidMaiaAccessToken,
    getActiveTenantId,
    tenantHeaderFor,
    endBrokerSession,
    clearBrokerSession,
  };
}

// ---------------------------------------------------------------------------
// Default singleton wired to real Auth0 / browser dependencies.
// ---------------------------------------------------------------------------

type SubjectTokenGetter = () => Promise<string>;

let subjectTokenGetter: SubjectTokenGetter | null = null;

// Registered from App.tsx with `getAccessTokenSilently({ audience: broker })`.
export const setBrokerSubjectTokenGetter = (
  getter: SubjectTokenGetter | null,
): void => {
  subjectTokenGetter = getter;
};

const REDIRECT_THROTTLE_MS = 30_000;
const REDIRECT_TIMESTAMP_KEY = 'maia_broker_redirect_at';
const LOGIN_PATH = '/login';

// Route to login on an unrenewable session, throttled so a redirect can't loop.
function triggerReauth(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;

  const last = sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY);
  const lastTs = last ? parseInt(last, 10) : NaN;
  if (!Number.isNaN(lastTs) && Date.now() - lastTs < REDIRECT_THROTTLE_MS) {
    return;
  }
  sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
  window.location.href = window.location.origin + LOGIN_PATH;
}

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const session = createBrokerSession({
  now: () => Date.now(),
  storage: browserStorage(),
  client: {
    exchangeAuth0Token,
    exchangeForTenant,
    refreshTokens,
    revokeTokens,
  },
  getSubjectToken: () => {
    if (!subjectTokenGetter) {
      return Promise.reject(new Error('broker subject token getter not set'));
    }
    return subjectTokenGetter();
  },
  scheduleRefresh: (delayMs, run) => {
    const id = setTimeout(run, delayMs);
    return () => clearTimeout(id);
  },
  onSessionLost: triggerReauth,
});

export const ensureSession = session.ensureSession;
export const ensureTenant = session.ensureTenant;
export const getValidMaiaAccessToken = session.getValidMaiaAccessToken;
export const getActiveTenantId = session.getActiveTenantId;
export const tenantHeaderFor = session.tenantHeaderFor;
export const endBrokerSession = session.endBrokerSession;
export const clearBrokerSession = session.clearBrokerSession;
